import type { CMSData, CmsPage, CMSSection } from "../cms/models";
import type {
  Article,
  ArticleComponentConfig,
  ArticleContext,
  ArticleOverviewContext,
  Blog,
  BlogData,
} from "./models";
import {
  articleExcerpt,
  authorPath,
  authorSlug,
  blogPath,
  blogTopics,
  newStyleArticlePath,
  newStyleBlogs,
  normalizeBlogPath,
  topicPath,
} from "./article-paths";

export function normalizePageSlug(slug: string): string {
  return "/" + slug.split("/").filter(Boolean).join("/");
}

export function articleUrl(
  detailsPageSlug: string,
  articleSlug: string,
): string {
  const base = normalizePageSlug(detailsPageSlug).replace(/\/$/, "");
  return `${base}/articles/${encodeURIComponent(articleSlug)}`;
}

export function articleDetailsSlug(
  blog: BlogData | undefined,
  config: ArticleComponentConfig,
  article: Article,
  currentPage?: string,
): string | undefined {
  return (
    config.detailsPageSlug ||
    blog?.blogs.find((item) => item.id === article.blogId)?.detailsPageSlug ||
    currentPage
  );
}

export function isPublishedArticle(
  article: Article,
  now = Date.now(),
): boolean {
  const publishedAt = Date.parse(article.publishedAt ?? "");
  return (
    article.status === "published" &&
    Number.isFinite(publishedAt) &&
    publishedAt <= now &&
    /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(article.slug)
  );
}

/** Defence in depth: no draft content reaches client components or public RSC payloads. */
export function publicBlogData(
  data?: BlogData,
  now = Date.now(),
): BlogData | undefined {
  if (!data) return undefined;
  const seen = new Set<string>();
  const posts = (data.posts ?? []).filter((post) => {
    if (!isPublishedArticle(post, now) || seen.has(post.slug)) return false;
    seen.add(post.slug);
    return true;
  });
  const authorIds = new Set(posts.flatMap((post) => post.authorIds ?? []));
  return {
    version: 1,
    blogs: data.blogs ?? [],
    ...(Array.isArray(data.topics) ? { topics: data.topics } : {}),
    authors: (data.authors ?? []).filter((author) => authorIds.has(author.id)),
    posts,
  };
}

function containsArticleHeader(node: any): boolean {
  return (
    (node?.type === "ArticleComponent" &&
      node.config?.family === "post-header") ||
    (node?.items ?? []).some(containsArticleHeader)
  );
}

/** A legacy details destination; an artikelside's template page (`type: "article"`) never is one. */
export function isArticleDetailsPage(
  page: Pick<CmsPage, "sections"> & { type?: string },
): boolean {
  return (
    page.type !== "article" &&
    (page.sections ?? []).some((section) =>
      containsArticleHeader(section.specifics?.rootComponent),
    )
  );
}

export function canonicalArticlePath(
  cms: Pick<CMSData, "pages" | "blog">,
  article: Article,
  fallback: string,
): string {
  const path = newStyleArticlePath(cms.blog, article);
  if (path) return path;
  const selected =
    article.seo?.detailsPageSlug ??
    cms.blog?.blogs.find((blog) => blog.id === article.blogId)?.detailsPageSlug;
  const page = cms.pages.find(
    (page) =>
      normalizePageSlug(page.slug ?? "") ===
        normalizePageSlug(selected ?? fallback) && isArticleDetailsPage(page),
  );
  return articleUrl(page?.slug ?? fallback, article.slug);
}

export interface ResolvedArticlePage {
  page: CmsPage;
  articleContext?: ArticleContext;
  /** An artikelside's overview address: which topic, author and page of it. */
  overviewContext?: ArticleOverviewContext;
  redirect?: string;
  /** A topic or author overview: the canonical path, and the name and text it puts before the page's own. */
  overview?: { canonicalPath: string; title?: string; description?: string };
}

const isOrdinaryPage = (page: Pick<CmsPage, "type">) =>
  page.type !== "footer" && page.type !== "article";

/** An artikelside's overview: the ordinary page at its path. */
export function blogOverviewPage(
  cms: Pick<CMSData, "pages">,
  blog: Pick<Blog, "path">,
): CmsPage | undefined {
  const path = blogPath(blog);
  return path
    ? cms.pages.find(
        (page) =>
          isOrdinaryPage(page) &&
          !!page.slug &&
          normalizeBlogPath(normalizePageSlug(page.slug)) === path,
      )
    : undefined;
}

/** The `type: "article"` page an artikelside renders its articles with. */
export function blogTemplatePage(
  cms: Pick<CMSData, "pages">,
  blog: Pick<Blog, "templatePageId">,
): CmsPage | undefined {
  return blog.templatePageId
    ? cms.pages.find(
        (page) => page.type === "article" && page.id === blog.templatePageId,
      )
    : undefined;
}

function newStyleArticleContext(
  cms: Pick<CMSData, "pages" | "blog">,
  blog: Blog,
  article: Article,
): ArticleContext {
  const path = blogPath(blog)!;
  return {
    article,
    detailsPageSlug: path,
    canonicalPath: `${path}/${article.slug}`,
    newStyle: true,
    backPath: blogOverviewPage(cms, blog) ? path : "/",
  };
}

/** Routes an article of a new-style artikelside: its template page, or nothing when the artikelside has none. */
function articleTarget(cms: Pick<CMSData, "pages" | "blog">, article: Article) {
  const blog = cms.blog?.blogs.find((item) => item.id === article.blogId);
  const page = blog && blogPath(blog) ? blogTemplatePage(cms, blog) : undefined;
  return blog && page
    ? { page, context: newStyleArticleContext(cms, blog, article) }
    : undefined;
}

function pageNumber(rest: string[]): number | undefined {
  if (!rest.length) return 1;
  return rest.length === 2 &&
    rest[0] === "side" &&
    /^[1-9]\d{0,3}$/.test(rest[1])
    ? Number(rest[1])
    : undefined;
}

/** `{path}`, `/side/n`, `/emne/x[/side/n]`, `/forfatter/y[/side/n]` and `/{slug}` of one new-style artikelside. */
function resolveBlogAddress(
  cms: Pick<CMSData, "pages" | "blog">,
  blog: Blog,
  rest: string[],
  posts: Article[],
): ResolvedArticlePage | undefined {
  const path = blogPath(blog)!;
  const own = posts.filter((post) => post.blogId === blog.id);
  const overviewAt = (
    base: string,
    parts: string[],
    extra: Partial<ArticleOverviewContext>,
    named?: { title: string; description?: string },
  ): ResolvedArticlePage | undefined => {
    const page = blogOverviewPage(cms, blog);
    const number = pageNumber(parts);
    if (!page || !number) return undefined;
    if (number === 1 && parts.length) return { page, redirect: base };
    return {
      page,
      overviewContext: { blogId: blog.id, pageNumber: number, ...extra },
      overview: {
        canonicalPath: number > 1 ? `${base}/side/${number}` : base,
        ...(named ?? {}),
      },
    };
  };
  if (rest[0] === "side") return overviewAt(path, rest, {});
  if (rest[0] === "emne") {
    const topics = blog.topicPages !== false ? blogTopics(cms.blog) : [];
    const topic = topics.find((item) => item.slug === rest[1]);
    const renamed = topic
      ? undefined
      : topics.find(
          (item) =>
            Array.isArray(item.previousSlugs) &&
            item.previousSlugs.includes(rest[1]),
        );
    const target = topic ?? renamed;
    if (!target || !own.some((post) => post.topicIds?.includes(target.id)))
      return undefined;
    const resolved = overviewAt(
      topicPath(path, target),
      rest.slice(2),
      { topicId: target.id },
      { title: target.name, description: target.description },
    );
    return renamed && resolved
      ? {
          page: resolved.page,
          redirect: resolved.overview?.canonicalPath ?? topicPath(path, target),
        }
      : resolved;
  }
  if (rest[0] === "forfatter") {
    const author =
      blog.authorPages === true && rest[1]
        ? (cms.blog?.authors ?? []).find((item) => authorSlug(item) === rest[1])
        : undefined;
    return author && own.some((post) => post.authorIds?.includes(author.id))
      ? overviewAt(
          authorPath(path, author),
          rest.slice(2),
          { authorId: author.id },
          { title: author.name },
        )
      : undefined;
  }
  if (rest.length !== 1) return undefined;
  const article = own.find((post) => post.slug === rest[0]);
  const renamed = article
    ? undefined
    : own.find((post) => post.previousSlugs?.includes(rest[0]));
  const target =
    article || renamed ? articleTarget(cms, (article ?? renamed)!) : undefined;
  if (!target) return undefined;
  return article
    ? { page: target.page, articleContext: target.context }
    : { page: target.page, redirect: target.context.canonicalPath };
}

/** The steps of the artikelside addresses after an exact page: its own addresses, moved articles, legacy links. */
function resolveNewStyleAddress(
  cms: Pick<CMSData, "pages" | "blog">,
  segments: string[],
): ResolvedArticlePage | undefined {
  const blogs = newStyleBlogs(cms.blog);
  if (!blogs.length) return undefined;
  const request = normalizeBlogPath("/" + segments.join("/"));
  if (!request) return undefined;
  const posts = publicBlogData(cms.blog)?.posts ?? [];
  const blog = blogs
    .filter((item) => {
      const path = blogPath(item)!;
      return request === path || request.startsWith(path + "/");
    })
    .sort((a, b) => blogPath(b)!.length - blogPath(a)!.length)[0];
  if (blog) {
    const rest = request
      .slice(blogPath(blog)!.length)
      .split("/")
      .filter(Boolean);
    const resolved = rest.length
      ? resolveBlogAddress(cms, blog, rest, posts)
      : undefined;
    if (resolved) return resolved;
  }
  const moved = posts.find((post) =>
    (post.previousPaths ?? []).some(
      (path) => normalizeBlogPath(path) === request,
    ),
  );
  const movedTarget = moved && articleTarget(cms, moved);
  if (movedTarget)
    return {
      page: movedTarget.page,
      redirect: movedTarget.context.canonicalPath,
    };
  if (segments.length >= 2 && segments.at(-2) === "articles") {
    const slug = segments.at(-1)!;
    const legacy =
      posts.find((post) => post.slug === slug) ??
      posts.find((post) => post.previousSlugs?.includes(slug));
    const target =
      legacy && newStyleArticlePath(cms.blog, legacy)
        ? articleTarget(cms, legacy)
        : undefined;
    if (target)
      return { page: target.page, redirect: target.context.canonicalPath };
  }
  return undefined;
}

/**
 * Exact CMS pages keep precedence (an artikelside's template page is never one); then the addresses of artikelsider
 * with a `path`; then a terminal `/articles/<slug>` on a legacy details page.
 */
export function resolveArticlePage(
  cms: Pick<CMSData, "pages" | "blog">,
  segments: string[] = [],
): ResolvedArticlePage | undefined {
  const exact = cms.pages.find(
    (page) =>
      page.slug &&
      page.type !== "article" &&
      page.slug.replace("/", "") === segments.join("/"),
  );
  if (exact) {
    const blog =
      exact.type !== "footer"
        ? newStyleBlogs(cms.blog).find(
            (item) =>
              blogPath(item) ===
              normalizeBlogPath(normalizePageSlug(exact.slug)),
          )
        : undefined;
    return blog
      ? {
          page: exact,
          overviewContext: { blogId: blog.id, pageNumber: 1 },
        }
      : { page: exact };
  }
  if (!cms.blog) return undefined;
  const addressed = resolveNewStyleAddress(cms, segments);
  if (addressed) return addressed;
  if (segments.length < 2 || segments.at(-2) !== "articles") return undefined;
  const base = normalizePageSlug(segments.slice(0, -2).join("/"));
  const page = cms.pages.find(
    (page) =>
      normalizePageSlug(page.slug ?? "") === base && isArticleDetailsPage(page),
  );
  if (!page) return undefined;
  const slug = segments.at(-1)!;
  const posts = (publicBlogData(cms.blog)?.posts ?? []).filter(
    (post) => !newStyleArticlePath(cms.blog, post),
  );
  const article = posts.find((post) => post.slug === slug);
  if (!article) {
    const renamed = posts.find((post) => post.previousSlugs?.includes(slug));
    return renamed
      ? { page, redirect: articleUrl(base, renamed.slug) }
      : undefined;
  }
  return {
    page,
    articleContext: {
      article,
      detailsPageSlug: base,
      canonicalPath: canonicalArticlePath(cms, article, base),
    },
  };
}

/** The overview's `?soeg=` search, at most 100 characters. */
export function withOverviewSearch(
  context: ArticleOverviewContext | undefined,
  searchParams?: { [key: string]: string | string[] | undefined },
): ArticleOverviewContext | undefined {
  const raw = searchParams?.soeg;
  const search = (Array.isArray(raw) ? raw[0] : raw)?.trim().slice(0, 100);
  return context && search ? { ...context, search } : context;
}

export function sectionVisible(
  section: Pick<CMSSection<any>, "visibility">,
  article?: ArticleContext,
): boolean {
  return section.visibility === "article"
    ? !!article
    : section.visibility === "base"
      ? !article
      : true;
}

export function selectArticles(
  posts: Article[],
  config: ArticleComponentConfig,
  current?: Article,
): Article[] {
  let selected = posts.filter(
    (post) =>
      (!config.blogId || post.blogId === config.blogId) &&
      (!config.category || post.category === config.category) &&
      (!config.featuredOnly || post.featured) &&
      (config.family !== "related" || post.id !== current?.id),
  );
  if (config.articleIds?.length) {
    selected = config.articleIds
      .map((id) => selected.find((post) => post.id === id))
      .filter((post): post is Article => !!post);
  } else {
    selected = [...selected].sort((a, b) => {
      const related = config.family === "related" && current;
      const relevance = related
        ? Number(b.category === current.category) -
          Number(a.category === current.category)
        : 0;
      return (
        relevance || Date.parse(b.publishedAt!) - Date.parse(a.publishedAt!)
      );
    });
  }
  return config.limit
    ? selected.slice(0, Math.max(1, Math.min(100, config.limit)))
    : selected;
}

export function safeArticleLink(value?: string): string | undefined {
  if (!value) return undefined;
  const trimmed = value.trim();
  if (
    [...trimmed].some(
      (char) =>
        char.charCodeAt(0) <= 32 || char.charCodeAt(0) === 127 || char === "\\",
    )
  )
    return undefined;
  return /^(https?:\/\/|mailto:|tel:|\/(?!\/)|#)/i.test(trimmed)
    ? trimmed
    : undefined;
}

export function buildArticleJsonLd(
  context: ArticleContext,
  data: BlogData,
  baseUrl: string,
  publisher: string,
) {
  const article = context.article;
  const image = context.newStyle
    ? article.seo?.image?.url || article.cover?.url
    : article.cover?.url;
  return {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: article.title,
    description: context.newStyle
      ? article.seo?.description || articleExcerpt(article)
      : (article.seo?.description ?? article.excerpt),
    url: `${baseUrl}${context.canonicalPath}`,
    mainEntityOfPage: `${baseUrl}${context.canonicalPath}`,
    datePublished: article.publishedAt,
    dateModified: article.updatedAt ?? article.publishedAt,
    ...(image ? { image: [image] } : {}),
    author: data.authors
      .filter((author) => article.authorIds?.includes(author.id))
      .map((author) => ({
        "@type": "Person",
        name: author.name,
        ...(safeArticleLink(author.url) ? { url: author.url } : {}),
      })),
    publisher: { "@type": "Organization", name: publisher },
  };
}

const newestDate = (posts: Article[]) =>
  posts
    .map((post) => post.updatedAt ?? post.publishedAt)
    .filter((date): date is string => Number.isFinite(Date.parse(date ?? "")))
    .sort((a, b) => Date.parse(b) - Date.parse(a))[0];

/** Articles, topic pages and author pages of the artikelsider with a `path`; never their template pages. */
function newStyleSitemapEntries(
  cms: Pick<CMSData, "pages" | "blog">,
  baseUrl: string,
  posts: Article[],
) {
  return newStyleBlogs(cms.blog).flatMap((blog) => {
    const path = blogPath(blog)!;
    const own = posts.filter((post) => post.blogId === blog.id);
    const template = blogTemplatePage(cms, blog);
    const overview = blogOverviewPage(cms, blog);
    const listed =
      !!overview && overview.noindex !== true && overview.sitemap !== false;
    const entry = (url: string, lastModified?: string) => ({
      url: `${baseUrl}${url}`,
      lastModified,
      changeFrequency: "monthly" as const,
      priority: 0.6,
    });
    return [
      ...(template && template.noindex !== true
        ? own.map((post) =>
            entry(`${path}/${post.slug}`, post.updatedAt ?? post.publishedAt),
          )
        : []),
      ...(listed && blog.topicPages !== false
        ? blogTopics(cms.blog).flatMap((topic) => {
            const tagged = own.filter((post) =>
              post.topicIds?.includes(topic.id),
            );
            return tagged.length
              ? [entry(topicPath(path, topic), newestDate(tagged))]
              : [];
          })
        : []),
      ...(listed && blog.authorPages === true
        ? (cms.blog?.authors ?? []).flatMap((author) => {
            const written = own.filter((post) =>
              post.authorIds?.includes(author.id),
            );
            return written.length && authorSlug(author)
              ? [entry(authorPath(path, author), newestDate(written))]
              : [];
          })
        : []),
    ];
  });
}

export function articleSitemapEntries(
  cms: Pick<CMSData, "pages" | "blog">,
  baseUrl: string,
) {
  const posts = publicBlogData(cms.blog)?.posts ?? [];
  return [
    ...posts.flatMap((article) => {
      if (newStyleArticlePath(cms.blog, article)) return [];
      const page = cms.pages.find(
        (page) =>
          page.slug ===
            (article.seo?.detailsPageSlug ??
              cms.blog?.blogs.find((blog) => blog.id === article.blogId)
                ?.detailsPageSlug) && isArticleDetailsPage(page),
      );
      return page && page.noindex !== true
        ? [
            {
              url: `${baseUrl}${canonicalArticlePath(cms, article, page.slug)}`,
              lastModified: article.updatedAt ?? article.publishedAt,
              changeFrequency: "monthly" as const,
              priority: 0.6,
            },
          ]
        : [];
    }),
    ...newStyleSitemapEntries(cms, baseUrl, posts),
  ];
}

export function cmsArticleCacheSeconds(
  nextPublicationAt?: string,
  now = Date.now(),
): number {
  const scheduled = Date.parse(nextPublicationAt ?? "");
  return Number.isFinite(scheduled)
    ? Math.max(0, Math.min(60, (scheduled - now) / 1000))
    : 60;
}

export function normalizeArticleVideoUrl(value?: string): string | undefined {
  if (!safeArticleLink(value)) return undefined;
  if (/^(https?:\/\/|\/(?!\/)).*\.(mp4|webm)([?#]|$)/i.test(value!))
    return value;
  try {
    const url = new URL(value!);
    if (url.protocol !== "https:") return undefined;
    const youtube = [
      "youtube.com",
      "www.youtube.com",
      "m.youtube.com",
    ].includes(url.hostname);
    const id =
      url.hostname === "youtu.be"
        ? url.pathname.slice(1)
        : youtube
          ? url.pathname === "/watch"
            ? url.searchParams.get("v")
            : url.pathname.match(/^\/(?:embed|shorts)\/([a-zA-Z0-9_-]+)$/)?.[1]
          : undefined;
    if (id && /^[a-zA-Z0-9_-]{6,}$/.test(id))
      return "https://www.youtube.com/embed/" + id;
    if (
      ["vimeo.com", "www.vimeo.com", "player.vimeo.com"].includes(url.hostname)
    ) {
      const video = url.pathname.match(/^\/(?:video\/)?(\d+)$/)?.[1];
      if (video) return "https://player.vimeo.com/video/" + video;
    }
  } catch {
    return undefined;
  }
  return undefined;
}
export function articleListingConfigs(
  sections: unknown,
): Record<string, ArticleComponentConfig> {
  const result: Record<string, ArticleComponentConfig> = {};
  const visit = (node: unknown) => {
    if (Array.isArray(node)) return node.forEach(visit);
    if (!node || typeof node !== "object") return;
    const item = node as { type?: string; config?: ArticleComponentConfig };
    if (
      item.type === "ArticleComponent" &&
      item.config?.family === "collection"
    )
      result[item.config.listingKey ?? item.config.blogId ?? "articles"] =
        item.config;
    Object.values(node).forEach(visit);
  };
  visit(sections);
  return result;
}
export function articleDisplayData(
  blog?: BlogData,
  context?: ArticleContext,
): BlogData | undefined {
  const visible = publicBlogData(blog);
  if (
    !visible ||
    !context?.preview ||
    !blog?.posts.some((post) => post.id === context.article.id)
  )
    return visible;
  const posts = [
    context.article,
    ...visible.posts.filter((post) => post.id !== context.article.id),
  ];
  const authors = new Set(posts.flatMap((post) => post.authorIds ?? []));
  return {
    ...visible,
    posts,
    authors: blog.authors.filter((author) => authors.has(author.id)),
  };
}
